import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const FeatureNeighborhood = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Neighborhood Restaurant Discounts | LOCU"
        description="Redeem LOCU points at local restaurants, cafes, bars, and salons near you. Get up to 10% off dining bills. Browse spots within 5km of your home."
        path="/feature/neighborhood"
        keywords="restaurant discounts, local dining deals, neighborhood rewards, redeem points restaurants"
      />
      {/* Hero Section */}
      <div className="bg-black text-white py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Your neighborhood.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              Now rewarding.
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 max-w-2xl mx-auto mb-12">
            Use your LOCU points at local restaurants, cafes, and businesses. 
            Exclusive discounts, just for being a renter.
          </p>
          {!isAuthenticated && (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Explore Local
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="text-6xl font-bold text-black mb-2">5km</div>
              <div className="text-lg text-gray-600">Coverage</div>
              <div className="text-sm text-gray-400 mt-2">Discover spots near you</div>
            </div>
            <div className="text-center p-8 border-l border-r border-gray-200">
              <div className="text-6xl font-bold text-black mb-2">10%</div>
              <div className="text-lg text-gray-600">Up to Discount</div>
              <div className="text-sm text-gray-400 mt-2">Using LOCU points</div>
            </div>
            <div className="text-center p-8">
              <div className="text-6xl font-bold text-black mb-2">₹1=1</div>
              <div className="text-lg text-gray-600">Point Value</div>
              <div className="text-sm text-gray-400 mt-2">Simple conversion</div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            Redeem in 3 easy steps
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Browse</h3>
              <p className="text-gray-600">Discover restaurants and businesses near you. Sorted by distance and rating.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Choose</h3>
              <p className="text-gray-600">Select your discount level. Use more points for bigger savings.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Redeem</h3>
              <p className="text-gray-600">Show your code at checkout. Instant savings, no waiting.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Business Types */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            Where you can redeem
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { name: 'Restaurants', desc: 'Fine dining to casual eats' },
              { name: 'Cafes', desc: 'Coffee, tea & co-working' },
              { name: 'Bars', desc: 'Pubs, lounges & breweries' },
              { name: 'Salons', desc: 'Hair, spa & wellness' },
            ].map((biz, idx) => (
              <div key={idx} className="bg-gray-100 p-6 rounded-2xl">
                <h3 className="text-xl font-bold mb-2">{biz.name}</h3>
                <p className="text-gray-600 text-sm">{biz.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Value Prop */}
      <div className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Turn rent into savings
          </h2>
          <p className="text-xl text-gray-600">
            Use LOCU points at partner restaurants and cafes near you.
            The more rent you pay, the more you save on dining out.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-black text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Explore your neighborhood
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            See what local spots are waiting for you.
          </p>
          {!isAuthenticated ? (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Sign Up to Explore
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={() => navigate('/neighborhood')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Browse Neighborhood
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeatureNeighborhood;
